import React, { useEffect, useState } from "react";
import {
  TextField,
  Grid,
  Button,
  Typography,
  useMediaQuery,
  useTheme,
  Box,
} from "@mui/material";
import { styled } from "@mui/system";
import PhoneInput from "react-phone-input-2";
import "./phoneInput.scss";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { usePlaygroundContext } from "../Playground";
import { generate_otp } from "../../api/agent";
import OTPPopup from "./OtpPop";
import { freeEmailProviders } from "../../resources/emailList";

// Custom validation function for business emails
const isValidBusinessEmail = (email: string) => {
  // Basic email format validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  // Check basic format first
  if (!emailRegex.test(email)) {
    return false;
  }
  // Extract domain part
  const domain = email.split("@")[1];
  // List of common free email providers to block
  // const freeEmailProviders = [
  //   'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com',
  //   'aol.com', 'icloud.com', 'mail.com', 'protonmail.com'
  // ];
  // Check if the domain is from a free email provider
  if (freeEmailProviders.includes(domain.toLowerCase())) {
    return false;
  }
  return true;
};

interface infoProps {
  onNext: () => void;
  onPrevious: () => void;
}

const countryOptions = [
  { code: "US", dialCode: "+1", flag: "🇺🇸" },
  { code: "IN", dialCode: "+91", flag: "🇮🇳" },
  { code: "UK", dialCode: "+44", flag: "🇬🇧" },
];

const StyledGrid = styled(Grid)(({ theme }) => ({
  borderRadius: "8px",
  maxWidth: "1200px",
  color: "#fff",
  margin: "0 auto",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2, 1),
  },
}));

const Informations: React.FC<infoProps> = ({ onNext, onPrevious }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const playgroundContext = usePlaygroundContext();
  if (!playgroundContext) {
    throw new Error("PlaygroundContext is not available");
  }
  const { playground, setPlayground } = playgroundContext;
  const [countryCode, setCountryCode] = useState(countryOptions[0].dialCode);
  const [otp, setOtp] = useState(false);

  const handleClose = () => {
    setOtp(false);
  };
  const scrollToTop = () => {
    window.scroll(0, 0);
  };

  // Trigger scrollToTop when component is loaded
  useEffect(() => {
    scrollToTop();
  }, []);

  // Responsive input styles
  const inputStyles = {
    height: "42px",
    width: "100%",
    background: "#050D2E",
    border: "1px solid #424965",
    borderRadius: "4px",
    input: {
      padding: "10.5px 14px",
      color: "#fff",
      fontFamily: "Lato",
      fontSize: "14px",
      lineHeight: "18px",
    },
    "& .MuiInputLabel-root": {
      color: "#b0b7c3",
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "none",
      },
      "&:hover fieldset": {
        borderColor: "none",
      },
      "&.Mui-focused fieldset": {
        border: "0px solid #424965",
      },
    },
    "& input::placeholder": {
      opacity: 1,
      color: "#8C99CB",
    },
    // Error text styling
    "& .MuiFormHelperText-root": {
      color: "#f44336",
      marginLeft: 0,
      fontSize: { xs: "11px", sm: "12px" },
    },
  };

  return (
    <StyledGrid
      container
      spacing={2}
      mb={{ xs: 15, sm: 20, md: 30 }}
      sx={{
        background: "transparent",
        color: "white",
        py: 0.5,
        px: { xs: 1, sm: 2, md: 6 },
      }}
      justifyContent="center"
      alignItems="center"
    >
      <Grid item xs={12} style={{ paddingLeft: "0px" }}>
        <Typography
          className="page-title"
          variant={isMobile ? "h6" : "h5"}
          align={isMobile ? "center" : "left"}
          sx={{
            marginBottom: { xs: "15px", sm: "20px" },
            background: "linear-gradient(90deg, #538DFF, #A365F3)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            fontSize: { xs: "18px", sm: "20px", md: "24px" },
            fontWeight: "500",
            padding: { xs: "0 10px", sm: "0" },
            textAlign: "left",
          }}
        >
          Enter your information to experience the power of Eximius AI
        </Typography>
      </Grid>
      <Formik
        initialValues={{
          fullName: "",
          businessName: "",
          businessEmail: "",
          mobileNumber: "",
        }}
        validationSchema={Yup.object({
          fullName: Yup.string().required("Full Name is required"),
          businessName: Yup.string().required("Business Name is required"),
          businessEmail: Yup.string()
            .email("Invalid email address")
            .test(
              "is-valid-business-email",
              "Please provide a valid business email",
              (value) => isValidBusinessEmail(value || "")
            )
            .required("Business Email is required"),
          mobileNumber: Yup.string()
            .required("Mobile Number is required")
            .matches(
              /^\d{11,}$/,
              "Mobile number must contain at least 10 digits"
            ), // Modified for different country formats
        })}
        onSubmit={(values, { setSubmitting }) => {
          let payload = {
            companyName: values.businessName,
            fullName: values.fullName,
            companyEmail: values.businessEmail,
            phoneNumber: values.mobileNumber,
          };
          generate_otp(payload).then((response) => {
            setOtp(true);
          });
          setPlayground((prev) => {
            return {
              ...prev,
              companyName: values.businessName,
              fullName: values.fullName,
              companyEmail: values.businessEmail,
              phoneNumber: values.mobileNumber,
            };
          });
          // setSubmitting(false);
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
          handleSubmit,
        }) => (
          <Form>
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="fullName"
                  fullWidth
                  placeholder="Full Name*"
                  variant="outlined"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.fullName}
                  error={touched.fullName && Boolean(errors.fullName)}
                  helperText={touched.fullName && errors.fullName}
                  InputLabelProps={{ style: { color: "#424965" } }}
                  sx={inputStyles}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  name="businessName"
                  fullWidth
                  placeholder="Business name*"
                  variant="outlined"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.businessName}
                  error={touched.businessName && Boolean(errors.businessName)}
                  helperText={touched.businessName && errors.businessName}
                  InputLabelProps={{ style: { color: "#424965" } }}
                  sx={inputStyles}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  name="businessEmail"
                  fullWidth
                  placeholder="Business email*"
                  variant="outlined"
                  onBlur={handleBlur}
                  value={values.businessEmail}
                  error={touched.businessEmail && Boolean(errors.businessEmail)}
                  helperText={touched.businessEmail && errors.businessEmail}
                  onChange={handleChange}
                  InputLabelProps={{ style: { color: "#424965" } }}
                  sx={inputStyles}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    "& .react-tel-input .form-controlContactUs::placeholder": {
                      color: "#8C99CB",
                      opacity: 1,
                    },
                    "& .react-tel-input .form-controlContactUs": {
                      background: "#050D2E",
                      border: "1px solid #424965",
                      borderRadius: "4px",
                      padding: "10.5px 14px",
                      paddingLeft: "50px",
                      color: "#ffffff", // Matches input color
                      fontFamily: "Lato",
                      fontSize: isMobile ? "13px" : "14px",
                      lineHeight: "18px",
                      outline: "none",
                    },
                  }}
                >
                  <PhoneInput
                    placeholder="Mobile number*"
                    inputClass="form-controlContactUs"
                    containerClass="react-tel-input"
                    inputProps={{
                      required: true,
                      "data-testid": "input-phone",
                    }}
                    inputStyle={{
                      height: "42px",
                      width: "100%",
                      background: "#050D2E",
                      border: "1px solid #424965",
                      borderRadius: "4px",
                      padding: "10.5px 14px",
                      paddingLeft: "50px",
                      color: "#ffffff", // Ensure the text is white while typing
                      fontFamily: "Lato",
                      fontSize: isMobile ? "13px" : "14px",
                      lineHeight: "18px",
                      outline: "none",
                    }}
                    buttonStyle={{
                      background: "#050D2E",
                      border: "1px solid #424965",
                      borderRadius: "4px 0 0 4px",
                    }}
                    dropdownStyle={{
                      background: "#050D2E",
                      color: "grey",
                    }}
                    country={"us"}
                    preserveOrder={["preferredCountries"]}
                    onChange={(value) => {
                      setFieldValue("mobileNumber", value);
                    }}
                  />

                  {touched.mobileNumber && errors.mobileNumber && (
                    <Typography
                      color="error"
                      variant="caption"
                      sx={{
                        fontSize: { xs: "11px", sm: "12px" },
                        marginLeft: 0,
                        display: "block",
                        marginTop: "3px",
                      }}
                    >
                      {errors.mobileNumber}
                    </Typography>
                  )}
                </Box>
              </Grid>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 2,
                  marginLeft: 3,
                  marginTop: 3,
                }}
              >
                <Button
                  onClick={onPrevious}
                  variant="contained"
                  sx={{
                    backgroundColor: "#7A7A7A",
                    borderRadius: "30px",
                    px: { xs: 3, sm: 4 },
                    py: { xs: 1, sm: 1.5 },
                    fontSize: "14px",
                    fontWeight: "700",
                    color: "white",
                    textTransform: "none",
                    flex: 1, // Makes the button take up equal space in the row
                    "&:hover": {
                      backgroundColor: "#5a5a5a", // Hover effect
                    },
                  }}
                >
                  Previous
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  sx={{
                    backgroundColor: "#007BFF",
                    borderRadius: "30px",
                    px: { xs: 3, sm: 4 },
                    py: { xs: 1, sm: 1.5 },
                    fontSize: "14px",
                    fontWeight: "700",
                    color: "white",
                    textTransform: "none",
                    flex: 1, // Makes the button take up equal space in the row
                    "&:hover": {
                      backgroundColor: "#0056b3",
                    },
                  }}
                >
                  Next
                </Button>
              </Box>
            </Grid>
          </Form>
        )}
      </Formik>
      {otp && (
        <OTPPopup
          open={otp}
          onClose={() => handleClose()}
          email={playground.companyEmail}
          onNext={() => onNext()}
        />
      )}
    </StyledGrid>
  );
};

export default Informations;
